import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import BottomDrawerHeader from './components/BottomDrawerHeader';
import closeIcon from './close.svg';
import styles from './BottomDrawer.module.scss';
import clsx from 'clsx';
import { handleBackgroundScroll } from '../../../utils/utils';
import BottomDrawerFooter from './components/BottomDrawerFooter';

interface BottomDrawerProps {
    children?: React.ReactNode;
    heading?: string | React.ReactNode;
    allowClose?: boolean;
    onClose?: Function;
    height?: string;
    dontClose?: boolean;
    showHeading?: boolean;
    style?: React.CSSProperties;
    iconStyle?: React.CSSProperties;
    dialogLabel?: string;
}

function BottomDrawer({
    children,
    heading,
    allowClose = true,
    onClose,
    height,
    dontClose,
    showHeading,
    style,
    iconStyle,
    dialogLabel,
}: BottomDrawerProps) {
    const TIMING_MS = 500;
    const [closing, setClosing] = useState(false);

    useEffect(() => {
        handleBackgroundScroll(false);
        document.addEventListener('keydown', closeDrawerOnEsc);
        return () => {
            handleBackgroundScroll(true);
            document.removeEventListener('keydown', closeDrawerOnEsc);
        };
    }, []);

    const closeDrawerOnEsc = (e) => {
        if (!dontClose && e.key === 'Escape') closeDrawer();
    };

    const closeDrawer = () => {
        if (dontClose) {
            onClose?.();
            return;
        }
        setClosing(true);
        setTimeout(() => {
            onClose?.();
            setClosing(false);
        }, TIMING_MS - 1);
    };

    return createPortal(
        <div
            className={clsx(styles.DrawerBackdrop, {
                [styles.CloseDrawerBackdrop]: closing,
            })}
            onClick={closeDrawer}
            style={{ animationDuration: `${TIMING_MS}ms` }}
        >
            <div
                className={clsx(styles.DrawerBody, {
                    [styles.CloseDrawer]: closing,
                })}
                onClick={(e) => e.stopPropagation()}
                style={{ ...style, height: height ?? '', animationDuration: `${TIMING_MS}ms` }}
                {...(dialogLabel && { role: 'dialog', 'aria-modal': true, 'aria-label': dialogLabel })}
            >
                {showHeading && <BottomDrawerHeader onClose={allowClose ? closeDrawer : undefined}>{heading}</BottomDrawerHeader>}
                {!showHeading && allowClose && (
                    <img className={styles.closeIcon} src={closeIcon} alt="close" height="15px" onClick={closeDrawer} style={iconStyle} />
                )}
                {children}
            </div>
        </div>,
        document.body,
    );
}

BottomDrawer.Header = BottomDrawerHeader;
BottomDrawer.Footer = BottomDrawerFooter;

export default BottomDrawer;
